import { useTranslation } from 'react-i18next';

import {
  type CopyReport,
  type Database,
  type DatabaseTable,
  useImportDatabase,
  useImportTable,
  usePreviewDatabaseImport,
  usePreviewTableImport,
} from '@/entities/database';
import type { Workspace } from '@/entities/workspace';
import { ImportSnapshotDialog } from './ImportSnapshotDialog';

/** "Import a database from a file": a NEW database in a workspace where the viewer may create one. */
export function ImportDatabaseDialog({
  workspaces,
  workspaceId,
  onImported,
  onClose,
}: {
  /** Workspaces where the viewer may create databases. */
  workspaces: readonly Workspace[];
  workspaceId?: string | undefined;
  onImported: (database: Database, report: CopyReport) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const preview = usePreviewDatabaseImport();
  const importDatabase = useImportDatabase();
  return (
    <ImportSnapshotDialog
      title={t('databases.portability.import.title.database')}
      intro={t('databases.portability.import.intro.database')}
      destinations={workspaces.map((w) => ({
        value: w.id,
        label: w.workspace_type === 'personal' ? t('home.personalWorkspace') : w.name,
      }))}
      initialDestination={workspaceId ?? workspaces[0]?.id ?? ''}
      pickerLabel={t('databases.duplicate.workspace')}
      offerConfidential
      preview={(file, records, destination, signal) =>
        preview.mutateAsync({ workspaceId: destination, file, records, signal })
      }
      run={({ file, destination, records, name, confidential, signal, onProgress }) =>
        importDatabase.mutateAsync({
          workspaceId: destination,
          file,
          records,
          name,
          isConfidential: confidential,
          signal,
          onProgress,
        })
      }
      onImported={({ database, report }) => {
        onImported(database, report);
      }}
      onClose={onClose}
    />
  );
}

/** "Import a table from a file" into the database the viewer is looking at. */
export function ImportTableDialog({
  database,
  onImported,
  onClose,
}: {
  database: Database;
  onImported: (table: DatabaseTable, report: CopyReport) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const preview = usePreviewTableImport(database.id);
  const importTable = useImportTable(database.id);
  return (
    <ImportSnapshotDialog
      title={t('databases.portability.import.title.table')}
      intro={t('databases.portability.import.intro.table', { name: database.name })}
      initialDestination={database.id}
      offerConfidential={false}
      warnSourceConfidential={!database.is_confidential}
      preview={(file, records, _destination, signal) =>
        preview.mutateAsync({ file, records, signal })
      }
      run={({ file, records, name, signal, onProgress }) =>
        importTable.mutateAsync({ file, records, name, signal, onProgress })
      }
      onImported={({ table, report }) => {
        onImported(table, report);
      }}
      onClose={onClose}
    />
  );
}
